import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  AttributionEventFiltersInput,
  AttributionEventRecord,
  AttributionEventTypeValue,
  AttributionVariantValue,
  MembershipTypeValue,
  useGetAttributionEventsQuery,
} from "../../../hooks/useGetAttributionEventsQuery";

const EVENT_TYPE_OPTIONS: Array<{
  value: AttributionEventTypeValue;
  label: string;
}> = [
  { value: "REGISTER_PAGE_VISITED", label: "Register Page Visited" },
  { value: "ACCOUNT_CREATED", label: "Account Created" },
  { value: "MEMBERSHIP_SUBSCRIBED", label: "Membership Subscribed" },
  { value: "MEMBERSHIP_UPGRADED", label: "Membership Upgraded" },
];

const VARIANT_OPTIONS: Array<{ value: AttributionVariantValue; label: string }> = [
  { value: "CONTROL", label: "Control" },
  { value: "TEST", label: "Test" },
  { value: "UNKNOWN", label: "Unknown" },
];

const MEMBERSHIP_OPTIONS: Array<{ value: MembershipTypeValue; label: string }> = [
  { value: "FREE", label: "Free" },
  { value: "BASIC", label: "Guild" },
  { value: "GUILD_PLUS", label: "Guild+" },
  { value: "UNKNOWN", label: "Unknown" },
];

const toGraphQlDate = (dateInput: string, endOfDay: boolean): string => {
  const suffix = endOfDay ? "T23:59:59.999Z" : "T00:00:00.000Z";
  return `${dateInput}${suffix}`;
};

const humanizeEnum = (value?: string | null): string => {
  if (!value) {
    return "Unknown";
  }

  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getMembershipDisplayLabel = (
  membershipType?: MembershipTypeValue | null
): string => {
  switch (membershipType) {
    case "FREE":
      return "Free";
    case "BASIC":
      return "Guild";
    case "GUILD_PLUS":
      return "Guild+";
    case "UNKNOWN":
    default:
      return "Unknown";
  }
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "Unknown";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Invalid date";
  }

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const truncate = (value?: string | null, maxLength: number = 56): string => {
  if (!value) {
    return "Unknown";
  }

  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 3)}...`;
};

const eventTypeChipColor = (eventType: AttributionEventTypeValue) => {
  switch (eventType) {
    case "ACCOUNT_CREATED":
      return "success";
    case "MEMBERSHIP_SUBSCRIBED":
      return "primary";
    case "MEMBERSHIP_UPGRADED":
      return "warning";
    case "REGISTER_PAGE_VISITED":
    default:
      return "default";
  }
};

const variantChipColor = (variant: AttributionVariantValue) => {
  switch (variant) {
    case "CONTROL":
      return "primary";
    case "TEST":
      return "secondary";
    case "UNKNOWN":
    default:
      return "default";
  }
};

const isLandingPageSource = (sourceLandingPage?: string | null): boolean => {
  const normalized = sourceLandingPage?.toLowerCase().trim() || "";
  if (!normalized || normalized === "unknown") {
    return false;
  }

  return normalized.includes("lp.homedesignguild.com");
};

const getVariantDisplayLabel = (event: AttributionEventRecord): string => {
  if (!isLandingPageSource(event.sourceLandingPage)) {
    return "No LP";
  }

  if (event.variant === "CONTROL") {
    return "Control";
  }

  if (event.variant === "TEST") {
    return "Test";
  }

  return "No LP";
};

const getVariantDisplayColor = (event: AttributionEventRecord) => {
  const label = getVariantDisplayLabel(event);
  if (label === "Control") {
    return variantChipColor("CONTROL");
  }
  if (label === "Test") {
    return variantChipColor("TEST");
  }
  return "default";
};

const getEventSummary = (event: AttributionEventRecord): string => {
  const membershipLabel = getMembershipDisplayLabel(event.membershipType);

  if (event.eventType === "ACCOUNT_CREATED") {
    return "Account Created";
  }

  if (event.eventType === "REGISTER_PAGE_VISITED") {
    return "Register Page Visited";
  }

  if (event.eventType === "MEMBERSHIP_SUBSCRIBED") {
    return membershipLabel === "Unknown"
      ? "Membership Subscribed"
      : `Subscribed (${membershipLabel})`;
  }

  if (event.eventType === "MEMBERSHIP_UPGRADED") {
    return membershipLabel === "Unknown"
      ? "Membership Upgraded"
      : `Upgraded to ${membershipLabel}`;
  }

  return humanizeEnum(event.eventType);
};

const renderWithTooltip = (value?: string | null, maxLength?: number) => {
  const safeValue = value?.trim() || "Unknown";
  return (
    <Tooltip title={safeValue}>
      <span>{truncate(safeValue, maxLength)}</span>
    </Tooltip>
  );
};

export const AdminEventAttributionPage = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [eventType, setEventType] = useState<AttributionEventTypeValue | "">("");
  const [variant, setVariant] = useState<AttributionVariantValue | "">("");
  const [membershipType, setMembershipType] = useState<MembershipTypeValue | "">("");
  const [accountType, setAccountType] = useState("");
  const [occurredFrom, setOccurredFrom] = useState("");
  const [occurredTo, setOccurredTo] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const filters = useMemo<AttributionEventFiltersInput | undefined>(() => {
    const nextFilters: AttributionEventFiltersInput = {};

    if (eventType) {
      nextFilters.eventType = eventType;
    }
    if (variant) {
      nextFilters.variant = variant;
    }
    if (membershipType) {
      nextFilters.membershipType = membershipType;
    }
    if (accountType.trim()) {
      nextFilters.accountType = accountType.trim();
    }
    if (occurredFrom) {
      nextFilters.occurredFrom = toGraphQlDate(occurredFrom, false);
    }
    if (occurredTo) {
      nextFilters.occurredTo = toGraphQlDate(occurredTo, true);
    }
    if (debouncedSearchInput) {
      nextFilters.searchText = debouncedSearchInput;
    }

    return Object.keys(nextFilters).length ? nextFilters : undefined;
  }, [
    accountType,
    debouncedSearchInput,
    eventType,
    membershipType,
    occurredFrom,
    occurredTo,
    variant,
  ]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const queryVariables = useMemo(
    () => ({
      page,
      limit: rowsPerPage,
      filters,
    }),
    [filters, page, rowsPerPage]
  );

  const { events, totalCount, loading, error, refetch } =
    useGetAttributionEventsQuery(queryVariables);

  const errorMessage = useMemo(() => {
    if (!error) {
      return "";
    }

    return (
      error.graphQLErrors?.[0]?.message ||
      error.networkError?.message ||
      error.message ||
      "Unknown error"
    );
  }, [error]);

  const handleEventTypeChange = (event: SelectChangeEvent<string>) => {
    setEventType((event.target.value || "") as AttributionEventTypeValue | "");
  };

  const handleVariantChange = (event: SelectChangeEvent<string>) => {
    setVariant((event.target.value || "") as AttributionVariantValue | "");
  };

  const handleMembershipChange = (event: SelectChangeEvent<string>) => {
    setMembershipType((event.target.value || "") as MembershipTypeValue | "");
  };

  const handleResetFilters = () => {
    setEventType("");
    setVariant("");
    setMembershipType("");
    setAccountType("");
    setOccurredFrom("");
    setOccurredTo("");
    setSearchInput("");
    setDebouncedSearchInput("");
    setPage(1);
  };

  const hasActiveFilters = Boolean(
    eventType ||
      variant ||
      membershipType ||
      accountType.trim() ||
      occurredFrom ||
      occurredTo ||
      searchInput.trim()
  );

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          border: (theme) => `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: "#fcfdff",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack spacing={0.5}>
            <Typography variant="h4" fontWeight={700}>
              Attribution Events
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Immutable event stream for landing-page attribution, account creation, and membership lifecycle.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="text"
              startIcon={<RestartAltIcon />}
              onClick={handleResetFilters}
              disabled={!hasActiveFilters}
            >
              Reset Filters
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="eventId, anonymousId, landing page..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="event-type-label">Event</InputLabel>
              <Select
                labelId="event-type-label"
                value={eventType}
                label="Event"
                onChange={handleEventTypeChange}
              >
                <MenuItem value="">All</MenuItem>
                {EVENT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="variant-label">Variant</InputLabel>
              <Select
                labelId="variant-label"
                value={variant}
                label="Variant"
                onChange={handleVariantChange}
              >
                <MenuItem value="">All</MenuItem>
                {VARIANT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="membership-label">Membership</InputLabel>
              <Select
                labelId="membership-label"
                value={membershipType}
                label="Membership"
                onChange={handleMembershipChange}
              >
                <MenuItem value="">All</MenuItem>
                {MEMBERSHIP_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Account Type"
              placeholder="Homeowner / Designer"
              value={accountType}
              onChange={(event) => setAccountType(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Occurred From"
              type="date"
              value={occurredFrom}
              onChange={(event) => setOccurredFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Occurred To"
              type="date"
              value={occurredTo}
              onChange={(event) => setOccurredTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            Failed to load attribution events: {errorMessage}
          </Alert>
        ) : null}

        <TableContainer
          sx={{
            border: (theme) => `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            position: "relative",
          }}
        >
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                backgroundColor: "rgba(255,255,255,0.65)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : null}

          <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell>Occurred At</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>Membership</TableCell>
                <TableCell>Account Type</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>User Email</TableCell>
                <TableCell>Landing Page</TableCell>
                <TableCell>Event Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No events found for the current filter set.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event: AttributionEventRecord) => (
                  <TableRow key={event.eventId} hover>
                    <TableCell>{formatDate(event.occurredAt)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={humanizeEnum(event.eventType)}
                        color={eventTypeChipColor(event.eventType)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getVariantDisplayLabel(event)}
                        color={getVariantDisplayColor(event)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{getMembershipDisplayLabel(event.membershipType)}</TableCell>
                    <TableCell>{event.accountType || "Unknown"}</TableCell>
                    <TableCell>{renderWithTooltip(event.userName, 32)}</TableCell>
                    <TableCell>{renderWithTooltip(event.userEmail, 42)}</TableCell>
                    <TableCell>{renderWithTooltip(event.sourceLandingPage, 70)}</TableCell>
                    <TableCell>{getEventSummary(event)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={Math.max(0, page - 1)}
          onPageChange={(_, nextPage) => setPage(nextPage + 1)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(1);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default AdminEventAttributionPage;
